import { genId } from "../../../core/services/genId";
import { OnInit, OnDestroy, Optional, Directive } from "@angular/core";
import { FormGroup, FormControl, AbstractControl } from "@angular/forms";
import { Subscription, Observable, interval, BehaviorSubject } from "rxjs";
import { map, distinctUntilChanged } from "rxjs/operators";
import { FlowComponent } from "../../../flow/components/flow/flow.component";
import { QuestionKind } from "../../../common/models/flow.dto";
import { deepClone } from "../../../builder/models/editor.model";
import { ContextInteractionService } from "../../services/context-interaction.service";

@Directive()
export class BaseControlComponent implements OnInit, OnDestroy {

    componentData: any = { key: "" };
    componentContext: any;
    get controlId(): string {
        if (this.componentData) return this.componentData.key;
        return genId();
    }
    controlName: string = '';
    flowComponent: FlowComponent;
    formGroup: FormGroup;
    isEdit: boolean = false;
    _isEnabled: boolean = false;

    protected editorKey: string;
    
    private enabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    enabledObs$: Observable<boolean> = this.enabledSubject.asObservable();

    get isEnabled(): boolean {
        return this._isEnabled;
    };
    set isEnabled(value: boolean) {
        this._isEnabled = value;
    }

    get control(): AbstractControl {
        return this.formGroup.controls[this.componentData.key];
    }

    reviewNotesContext = "$.reviewNotes";

    public _isEnabledSubscription: Subscription;
    private _initialValueSubscription: Subscription;

    get questionType(): QuestionKind {
        return (this.componentData.type) ? this.componentData.type : QuestionKind.Empty;
    }

    get enableEditing(): boolean {
        let result: boolean = true;
        if (this.isInReview) {
            if (this.componentData.config && this.componentData.config.enableEditing != null) {
                result = this.componentData.config.enableEditing;
            } else {
                result = getReviewFormEditableDefault(this.componentData.type);
            }
            if (result && this.componentData.config && this.componentData.config.requireNotes != null) {
                let notes = this.flowComponent != null ? this.flowComponent.getNotes(this.componentData.contextTarget) : [];
                let recentNotes = notes.filter(n => (new Date(n.createdDate)).getDay() == (new Date()).getDay());
                result = !this.componentData.config.requireNotes || (recentNotes.length > 0);
            }
        } else {
            if (this.isReturned) {
                const icon = this.flowComponent.getNotes(this.componentData.contextTarget);
                result = icon && icon.length > 0;
            }
        }
        return result;
    }

    get isInReview(): boolean {
        let result: boolean = false;
        if (this.flowComponent && this.flowComponent.workflow) {
            result = this.flowComponent.workflow.isInReview();
        }
        return result;
    }

    get isReturned(): boolean {
        let result: boolean = false;
        if (this.flowComponent && this.flowComponent.workflow) {
            result = this.flowComponent.workflow.isReturned;
        }
        return result;
    }

    constructor(@Optional() public readonly contextService: ContextInteractionService) {
        let conf = window["PdfConfig"] as any;
        this.editorKey = (conf != null) ? conf.editorLicenseKey : "";
    }

	ngOnInit(): void {
		
        if (!this.formGroup) {
            let items = {};
            items[this.componentData.key] = new FormControl({});
            this.formGroup = new FormGroup(items);
        }

        if (this.isEdit || !this.componentData)
            return;

        if (this.componentData && this.componentData.contextTarget) {
            this.controlName = this.componentData.contextTarget.slice(2);
        }

        if (this.componentData.enabled === undefined)
            this.componentData.enabled = (): boolean => { return true; };
        this._isEnabledSubscription = interval(200).pipe(
            map(x => this.enableEditing && this.componentData.enabled()),
            distinctUntilChanged((o, n) => o === n))
            .subscribe(x => {
            this.toggleEnabled(x);
        });

        // Initialize the $.data context value.  It will be created if it does not exist.
        if (this.componentContext && this.componentData.contextTarget && !this.contextService.getContextData(this.componentData.contextTarget, this.componentContext))
            this.contextService.setContextData(this.componentData.contextTarget, { value: this.componentData.defaultValue, enabled: true, visible: true }, this.componentContext);

        let valueValue = "" as any;
        try {
            valueValue = this.contextService.getContextData((this.componentData.contextTarget + ".value"), this.componentContext);
        }
        catch (e) {

        }

		/*let valueExists = !(valueValue === "" || valueValue === undefined || valueValue === null || isNaN(valueValue));*/
		let valueExists = !(valueValue === "" || valueValue === undefined || valueValue === null || (typeof(valueValue) === 'number' && isNaN(valueValue)));// || valueValue === NaN);
        //Now check for whether the question context has a value, if not and it has an initialDataContextTarget value, try to copy that value into the question's value node
        this.setInitialValue(valueExists);
    }

    ngOnDestroy(): void {
        if (this._isEnabledSubscription)
            this._isEnabledSubscription.unsubscribe();
        if (this._initialValueSubscription)
            this._initialValueSubscription.unsubscribe();
    }


    ngAfterViewInit(): void {
    }

    toggleEnabled(enabled: boolean): void {
        let control = this.formGroup.get(this.componentData.key);
        let currentValue = this.isEnabled;
        if (enabled) {
            control.enable();
            this.isEnabled = true;
        } else {
            control.disable();
            this.isEnabled = false;
        }
        if (currentValue != this.isEnabled) {
            this.enabledSubject.next(this.isEnabled);
        }
    }

    get contextValue() {
        return this.getContext("value");
    }

    set contextValue(value) {
        this.storeContext(value, "value");
    }

    getContextValue(): any {
        return this.getContext("value");
    }

    setContextValue(value: any, fireEvent: boolean = true) {
        this.storeContext(value, "value", fireEvent);
    }

    get isVisible(): boolean {
        if (this.isEdit)
            return true;

        try {           
            return this.componentData.visible();
        }
        catch (e) { return true; }
    }


    getInitialValue(): any {
        return this.componentData.initialValue();
    }


    setInitialValue(valueExists: boolean): void {
        if (this.componentContext && this.componentData.contextTarget && !valueExists && this.componentData.initialValue) {
            let initial = this.getInitialValue();
            let value: any;
            if (initial instanceof Array || typeof initial == 'object') {
                value = deepClone(initial); //context location of initial value and new value become linked by reference if straight assignment is used when the value is an array or object
            } else {
                value = initial; 
            }
            this.contextService.setContextData(`${this.componentData.contextTarget}.value`, value, this.componentContext)
        }
    }

    storeContext(value: any, key: string, fireEvent: boolean = true): void {
        if (this.isEdit || !this.componentData)
            return;

        if (!this.componentData.contextTarget) {
            console.warn(`${this.componentData.type} - ${this.componentData.label} has no configured context.`);
            return;
        }

        if (!this.enableEditing) {
            return;
        }
        let target = `${this.componentData.contextTarget}.${key}`;
        if (key === this.componentData.contextTarget) {
            target = this.componentData.contextTarget;
        } else if (key === this.reviewNotesContext) {
            target = this.reviewNotesContext;
        }
        this.saveContextData(target, value, fireEvent);
    }

    getContext(key: string): any {
        if (this.isEdit || !this.componentData)
            return;

        if (!this.componentData.contextTarget)
            return;

        return this.getContextData(`${this.componentData.contextTarget}.${key}`);
    }

    getContextData(target: string): any {
        if (!target) return undefined;
        if (!this.componentContext) return ""; //should only happen in the builder
        return this.contextService.getContextData(target.trim(), this.componentContext);
    }

    getContextDataOrReturnTargetAsValue(target: string): any {
        if (!target) return "";
        try {
            return this.contextService.getContextData(target, this.componentContext);
        }
        catch (e) {
            return target;
        }
    }

    saveContextData(target: string, value: any, fireEvent: boolean = true): void {
        if (!this.componentContext)
            return;

        this.contextService.setContextData(target, value, this.componentContext, fireEvent);
    }

    change(): void {

        //updateValueAndValidity() checks all ancestors. for a larger for this could be performance hit. Ideally a component should be
        //encapuslated with its feature, and exposing them as properties for client code to set it as necessary.
        //TODO: enhancement candidate.
        //feels wrong to do it here but have not other choice.

       // this.formGroup.controls[this.componentData.key].updateValueAndValidity();
        this.control.updateValueAndValidity();
        this.updateChangeRules();

    }

    updateChangeRules(): void {
        if (this.componentData && this.componentData.changeRules) {
            //Try to build each rule individually
            let workingRules: any[] = [];
            let compiledRuleValues: any[] = [];
            for (let changeRule of this.componentData.changeRules) {
                try {
                    let builtRule = changeRule.rule();
                    workingRules.push(changeRule);
                    compiledRuleValues.push(builtRule);
                }
                catch (e) {
                    console.warn("An error occurred when trying to build the '" + changeRule.name + "' rule. The error was: " + e + " .")
                }
            }
            Promise.all(compiledRuleValues).then(values => {
                for (let v = 0; v < values.length; v++) {
                    try {
                        this.saveContextData(workingRules[v].contextTarget, values[v]);
                    }
                    catch (e) {
                        console.warn("An error occurred when trying to execute the '" + workingRules[v].name + "' rule. The error was:" + e + " .")
                    }
                }
            });
        }
    }
}

export function getReviewFormEditableDefault(type: QuestionKind): boolean {
    let result: boolean = false;
    switch (type) {
        case QuestionKind.ReviewComments:
        case QuestionKind.ChiefsSignature:
        case QuestionKind.ReportTemplateEditor:
        case QuestionKind.CertifiedMailLabelGenerator:
        case QuestionKind.ReportSelector:
        case QuestionKind.PdfGenerator:
            result = true;
            break;
        default:
            result = false;
            break;
    }
    return result;
}
